import * as DsIcon from "@equipmentshare/ds-icon";
import { saveAs } from "file-saver";
import { Either } from "fp";
import React from "react";

import { Button } from "~/ButtonV2";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Time } from "~/Time";

export type DownloadReportProps = {
  readonly route: Log.Route;
  readonly onComplete: () => void;
};
export function DownloadReport(props: DownloadReportProps): JSX.Element {
  const [running, setRunning] = React.useState(false);

  const onReportComplete = () => {
    props.onComplete();
    setRunning(false);
  };

  const refetch = DownloadReport.use(props.route, onReportComplete);

  return (
    <Button
      disabled={running}
      icon={<DsIcon.Icon name="$icon_download_line" size="$icon_size_4" />}
      type="text"
      style={{ padding: 0 }}
      onClick={() => {
        setRunning(true);
        void refetch();
      }}
    >
      &nbsp;Download PDF report
    </Button>
  );
}

export namespace DownloadReport {
  export const use = (route: Log.Route, setRunning: (arg: boolean) => void) => {
    return Remote.Fetch.use(
      ["daily-log-pdf", route.driver.id, route.dateTime],
      request(route),
      {
        enabled: false,
        cacheTime: 0,
        onSuccess: (result) => saveReport(result, getName(route)),
        onSettled: () => setRunning(false),
      }
    );
  };

  export const request = (route: Log.Route) => {
    const endpoint = `/elogs/driver/${
      route.driver.id
    }/logs-report/${Time.DateTime.toISOWithoutTime(route)}`;

    return flow(Request.fromConfig({ endpoint }), Request.Expect.status(200));
  };

  export const saveReport = (resp: Either<Error, Response>, filename: string) =>
    pipe(
      resp,
      Either.fold(
        () => console.log("Failed to retrieve report"),
        (response: Response) =>
          response.blob().then((value) => saveAs(value, filename))
      )
    );

  export const getName = (route: Log.Route) => {
    const parts = [
      route.driver.name?.first?.toLowerCase(),
      route.driver.name?.last?.toLowerCase(),
      Time.DateTime.toISOWithoutTime(route),
    ];
    return `daily_log_for_${parts.join("_")}.pdf`;
  };
}
